import { useEffect, useRef, useState } from 'react'

import type { TicketPhoto } from '@/api/dto/ticket'
import FullScreenImageSlide from '@/shared/components/fullScreen/FullScreenImageSlide'
import Icons from '@/shared/components/icon/Icons'
import Portal from '@/shared/components/Portal'
import Pagination from '@/shared/components/slider/components/Pagination'
import SliderCore from '@/shared/components/slider/components/SliderCore'
import type { SliderOptions } from '@/shared/components/slider/types/sliderOptions'
import type { SlideItem } from '@/shared/components/slider/view'
import { naverInAppRegex } from '@/shared/utils/regexPatterns'

interface ParkinglotPhoto {
  fileName: string
  thumbnail: string
  width: number
  height: number
}

export type Photo = ParkinglotPhoto | TicketPhoto

interface FullScreenImageViewerProps {
  currentSlide: number
  setCurrentSlide: React.Dispatch<React.SetStateAction<number>>
  isOpen: boolean
  items: SlideItem[]
  isAnimationStart?: boolean
  onClose: () => void
  id?: string
}

const FullScreenImageViewer = ({
  isOpen,
  items,
  onClose,
  isAnimationStart,
  id,
  currentSlide,
  setCurrentSlide
}: FullScreenImageViewerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isNotNaverInApp, setIsNotNaverInApp] = useState(false)

  const options: SliderOptions = {
    pagination: false
  }

  const onContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!wrapperRef.current) return

    const target = e.target as Element

    if (!wrapperRef.current.contains(target)) {
      onClose()
    }
  }

  useEffect(() => {
    const useragent = window.navigator.userAgent
    const isNotNaverInApp = useragent ? useragent.search(naverInAppRegex) === -1 : false

    setIsNotNaverInApp(isNotNaverInApp)
  }, [])

  const sliderProps = {
    options,
    items,
    slideComponent: FullScreenImageSlide,
    isNotNaverInApp,
    currentSlide,
    setCurrentSlide
  }

  if (isOpen) {
    return (
      <Portal targetId={id}>
        <div
          className={`fixed left-0 right-0 z-300 flex h-full justify-center transition-all duration-500 ${
            isAnimationStart ? 'top-0' : 'top-[100%]'
          }`}
          onClick={onContainerClick}
        >
          <div className={`relative h-full w-full max-w-[768px] bg-090`} ref={wrapperRef}>
            <div className="absolute left-0 right-0 top-0 z-20 flex h-[56px] items-center bg-dim-050 px-4">
              <span onClick={onClose}>
                <Icons.BackIcon stroke="#ffffff" className="cursor-pointer" />
              </span>
              <Pagination currentSlide={currentSlide} total={items.length} styles="absolute top-[17px] right-[16px]" />
            </div>
            <SliderCore {...sliderProps} />
          </div>
        </div>
      </Portal>
    )
  } else {
    return <></>
  }
}

export default FullScreenImageViewer
